import type { AbstractControl } from '@angular/forms';
import type { CompileAnswerResponse } from '../../api/compiling-api.service';
import {
  isSectionStructureObject,
  isSubSectionStructureObject,
  isWidgetStructureObject,
  type ModuleStructureObject,
  type WidgetMappedType,
  type WidgetStructureObject,
} from '../../api/interfaces';
import {
  DEFAULT_MULTIPLIER_OPTION_KEY,
  isDefaultMultiplierOption,
  multiplierValueError,
  type MultiplierOption,
} from '../../components/new-elements/MultiplierRadioButton/multiplier-answer';

/**
 * The widget types the AI compiler can fill. Both the per-question button and
 * the bulk "compila tutte le risposte" run are gated on this list — the backend
 * skips anything else with `UnsupportedWidgetType`.
 */
export const AUTO_COMPILE_SUPPORTED_TYPES: WidgetMappedType[] = [
  'TextBox',
  'RadioButton',
  'ComboBox',
  'MultiRadioButton',
  'MultiplierRadioButton',
];

export type ApplyCompiledAnswerResult =
  | { status: 'applied'; display: string }
  /** The compiler had nothing to propose; `justification` says why. */
  | { status: 'empty' }
  /** The proposed option is not among the widget's options any more. */
  | { status: 'invalid-option'; selectedValue: string }
  | { status: 'unsupported' };

/**
 * Why a suggestion could not be written, in the applicant's words. Shared so the
 * per-question button and the bulk report say the same thing.
 */
export function describeApplyFailure(
  result: ApplyCompiledAnswerResult,
): string {
  if (result.status === 'invalid-option') {
    return `L'opzione "${result.selectedValue}" non è più disponibile per questa domanda.`;
  }
  if (result.status === 'empty') {
    return 'Nessuna risposta è stata proposta per questa domanda.';
  }
  return 'Questa domanda non supporta la compilazione automatica.';
}

interface TabularOption {
  label: string;
  value: string | number;
}

/** One radio group of a MultiRadioButton, as its customization declares it. */
interface TabularOptionGroup {
  key: string;
  label?: string;
  defaultValue?: string | number | null;
  options: TabularOption[];
}

/**
 * Writes one compiled suggestion into its form control.
 *
 * Nothing is persisted server-side by either compile endpoint, so the control
 * is marked dirty: the applicant still has to hit "Salva", and the unsaved-
 * changes prompts have to see the change.
 */
export function applyCompiledAnswer(
  widget: WidgetStructureObject | undefined,
  formControl: AbstractControl | undefined,
  response: CompileAnswerResponse,
): ApplyCompiledAnswerResult {
  if (!widget || !formControl) return { status: 'unsupported' };

  if (widget._mappedType === 'TextBox') {
    const answer = response.answer;
    if (answer == null || answer === '') return { status: 'empty' };

    setAndMarkDirty(formControl, answer);
    return { status: 'applied', display: answer };
  }

  if (
    widget._mappedType === 'RadioButton' ||
    widget._mappedType === 'ComboBox'
  ) {
    const selectedValue = response.selectedValue;
    if (selectedValue == null || selectedValue === '')
      return { status: 'empty' };

    const match = findOption(widget, selectedValue);
    if (!match) return { status: 'invalid-option', selectedValue };

    setAndMarkDirty(formControl, match.value);
    return {
      status: 'applied',
      display: response.selectedLabel || match.label || String(match.value),
    };
  }

  if (widget._mappedType === 'MultiRadioButton') {
    const picks = resolveGroupPicks(widget, response);
    if (picks.status !== 'ok') return picks;

    // The control holds one object keyed by group. Only the groups the model
    // answered are touched: a group it found nothing for keeps whatever it
    // has, default or the applicant's own pick.
    const current = formControl.value;
    const base =
      current && typeof current === 'object' && !Array.isArray(current)
        ? (current as Record<string, unknown>)
        : {};

    setAndMarkDirty(formControl, {
      ...base,
      ...Object.fromEntries(picks.picks.map((p) => [p.key, p.value])),
    });
    return { status: 'applied', display: describeGroupPicks(picks.picks) };
  }

  if (widget._mappedType === 'MultiplierRadioButton') {
    const pick = resolveMultiplierPick(widget, response);
    if (pick.status !== 'ok') return pick;

    setAndMarkDirty(formControl, {
      option: pick.option.key,
      value: pick.value,
    });
    return { status: 'applied', display: describeMultiplierPick(pick) };
  }

  return { status: 'unsupported' };
}

/**
 * What `applyCompiledAnswer` *would* show, without touching the form. Lets the
 * bulk review step preview a suggestion before the applicant accepts it.
 */
export function previewCompiledAnswer(
  widget: WidgetStructureObject | undefined,
  response: CompileAnswerResponse,
): string | null {
  if (!widget) return null;

  if (widget._mappedType === 'TextBox') {
    return response.answer || null;
  }

  if (widget._mappedType === 'MultiRadioButton') {
    // Lenient where the apply is strict: a pick the widget no longer offers is
    // still previewed by what the model said, exactly as the tabular preview
    // below shows an unmatched `selectedValue`.
    const groups = groupsOf(widget);
    const lines = (response.groups ?? [])
      .filter((g) => g.selectedValue != null && g.selectedValue !== '')
      .map((g) => {
        const group = groups.find((x) => x.key === g.groupId);
        const match = group && findIn(group.options, g.selectedValue as string);
        return `${g.groupLabel || group?.label || g.groupId}: ${
          g.selectedLabel || match?.label || String(g.selectedValue)
        }`;
      });
    return lines.length ? lines.join('\n') : null;
  }

  if (widget._mappedType === 'MultiplierRadioButton') {
    // Lenient like the tabular preview: an unknown key is still shown as the
    // model said it. The default carries no quantity worth showing.
    const selectedValue = response.selectedValue;
    if (selectedValue == null || selectedValue === '') return null;

    const option = findMultiplierOption(widget, selectedValue);
    const label = response.selectedLabel || option?.label || selectedValue;
    const isDefault = option
      ? isDefaultMultiplierOption(option)
      : isDefaultKey(selectedValue);
    if (isDefault || response.numericValue == null) return label;
    return `${label}: ${formatQuantity(response.numericValue)}`;
  }

  const selectedValue = response.selectedValue;
  if (selectedValue == null || selectedValue === '') return null;

  const match = findOption(widget, selectedValue);
  return response.selectedLabel || match?.label || String(selectedValue);
}

/**
 * Whether a compiled answer carries anything `applyCompiledAnswer` could write.
 *
 * Decided on the payload alone, not the widget type, so the bulk review can
 * sort an entry before it knows whether the structure still has the widget: a
 * descriptive answer is a non-empty `answer`, a tabular one a non-empty
 * `selectedValue`, a multi-radio one at least one group with a non-empty
 * `selectedValue` — the same emptiness `applyCompiledAnswer` reports as
 * `empty`. A MultiplierRadioButton answers in `selectedValue` too, so a pick of
 * its default option is a proposal like any other. A payload with none of
 * these is the model saying, in its `justification`, that the documents do not
 * cover the question.
 */
export function hasProposal(response: CompileAnswerResponse): boolean {
  const { answer, selectedValue, groups } = response;

  return (
    (answer != null && answer !== '') ||
    (selectedValue != null && selectedValue !== '') ||
    (groups ?? []).some(
      (g) => g.selectedValue != null && g.selectedValue !== '',
    )
  );
}

/**
 * Whether a MultiRadioButton's current value is the applicant's, read the way
 * the backend's `IsAnswered` reads it for the batch: the form seeds the control
 * with one object holding each group's `defaultValue`, so the object alone
 * says nothing — it is an answer once *one* group holds something non-empty
 * other than its own default. The per-question button asks this before
 * warning about an overwrite; for any other widget the plain "is there a
 * value" test is the caller's.
 */
export function hasApplicantGroupPick(
  widget: WidgetStructureObject,
  value: unknown,
): boolean {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;

  const picks = value as Record<string, unknown>;

  return groupsOf(widget).some((group) => {
    const pick = picks[group.key];
    if (pick == null || pick === '') return false;
    if (group.defaultValue == null || group.defaultValue === '') return true;
    return String(pick) !== String(group.defaultValue);
  });
}

/**
 * Whether a MultiplierRadioButton holds the applicant's answer, read the way
 * the backend's `IsAnswered` reads it: the form seeds the default option, so
 * the widget is answered once another option is picked.
 */
export function hasApplicantMultiplierPick(value: unknown): boolean {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;

  const option = (value as { option?: unknown }).option;
  return (
    typeof option === 'string' && option.trim() !== '' && !isDefaultKey(option)
  );
}

interface GroupPick {
  key: string;
  label: string;
  value: string | number;
  display: string;
}

/**
 * The model's per-group picks, resolved onto the widget's groups and options.
 * Strict, like the tabular apply: one pick the widget cannot hold refuses the
 * whole suggestion rather than writing half of it — the backend already
 * resolves each pick against the group's options, so this only trips on a
 * form revision that moved on since the batch ran. Groups the model answered
 * with null are simply not in the result.
 */
function resolveGroupPicks(
  widget: WidgetStructureObject,
  response: CompileAnswerResponse,
):
  | { status: 'ok'; picks: GroupPick[] }
  | { status: 'empty' }
  | { status: 'invalid-option'; selectedValue: string } {
  const groups = groupsOf(widget);
  const picks: GroupPick[] = [];

  for (const entry of response.groups ?? []) {
    const selectedValue = entry.selectedValue;
    if (selectedValue == null || selectedValue === '') continue;

    const group = groups.find((g) => g.key === entry.groupId);
    const match = group && findIn(group.options, selectedValue);
    if (!group || !match) return { status: 'invalid-option', selectedValue };

    picks.push({
      key: group.key,
      label: entry.groupLabel || group.label || group.key,
      value: match.value,
      display: entry.selectedLabel || match.label || String(match.value),
    });
  }

  return picks.length ? { status: 'ok', picks } : { status: 'empty' };
}

/** One line per answered group: "Etichetta gruppo: opzione". */
function describeGroupPicks(picks: GroupPick[]): string {
  return picks.map((p) => `${p.label}: ${p.display}`).join('\n');
}

interface MultiplierPick {
  status: 'ok';
  option: MultiplierOption;
  value: number;
  display: string;
}

/**
 * The model's pick resolved onto the widget. Strict, like the multi-radio
 * apply: an unknown key, a quantity outside the option's range or a fraction
 * in integer mode refuses the whole suggestion. The backend already validates
 * the suggestion, so this only trips on a form revision that moved on since.
 * A default pick writes value 0 whatever quantity came with it.
 */
function resolveMultiplierPick(
  widget: WidgetStructureObject,
  response: CompileAnswerResponse,
):
  | MultiplierPick
  | { status: 'empty' }
  | { status: 'invalid-option'; selectedValue: string } {
  const selectedValue = response.selectedValue;
  if (selectedValue == null || selectedValue === '') return { status: 'empty' };

  const option = findMultiplierOption(widget, selectedValue);
  if (!option) return { status: 'invalid-option', selectedValue };

  const display = response.selectedLabel || option.label || option.key;
  if (isDefaultMultiplierOption(option))
    return { status: 'ok', option, value: 0, display };

  // Out of range or fractional: named with its quantity, which is what the
  // widget can no longer take.
  const value = response.numericValue;
  const decimal = !!(widget.customization as { decimal?: boolean }).decimal;
  if (value == null)
    return { status: 'invalid-option', selectedValue: display };
  if (multiplierValueError(option, value, decimal))
    return {
      status: 'invalid-option',
      selectedValue: `${display}: ${formatQuantity(value)}`,
    };

  return { status: 'ok', option, value, display };
}

/** "Etichetta: quantità", or just the label for the default option. */
function describeMultiplierPick(pick: MultiplierPick): string {
  return isDefaultMultiplierOption(pick.option)
    ? pick.display
    : `${pick.display}: ${formatQuantity(pick.value)}`;
}

/** Matched the way the backend matches it: trimmed, case-insensitive. */
function findMultiplierOption(
  widget: WidgetStructureObject,
  selectedValue: string,
): MultiplierOption | undefined {
  const customization = widget.customization as
    | { options?: MultiplierOption[] }
    | undefined;
  const key = selectedValue.trim().toLowerCase();

  return (customization?.options ?? []).find(
    (option) => (option.key ?? '').trim().toLowerCase() === key,
  );
}

function isDefaultKey(key: string): boolean {
  return key.trim().toLowerCase() === DEFAULT_MULTIPLIER_OPTION_KEY;
}

/** Italian digits, as the rest of the UI prints them. */
function formatQuantity(value: number): string {
  return value.toLocaleString('it-IT', { maximumFractionDigits: 3 });
}

function groupsOf(widget: WidgetStructureObject): TabularOptionGroup[] {
  const customization = widget.customization as
    | { groups?: TabularOptionGroup[] }
    | undefined;

  return customization?.groups ?? [];
}

function findOption(
  widget: WidgetStructureObject,
  selectedValue: string,
): TabularOption | undefined {
  const customization = widget.customization as
    | { options?: TabularOption[] }
    | undefined;

  return findIn(customization?.options ?? [], selectedValue);
}

function findIn(
  options: TabularOption[],
  selectedValue: string,
): TabularOption | undefined {
  return options.find(
    (option) => String(option.value) === String(selectedValue),
  );
}

function setAndMarkDirty(control: AbstractControl, value: unknown): void {
  control.setValue(value);
  control.markAsDirty();
}

/** One question, as the bulk-compile UI needs to name it to the applicant. */
export interface CompilableQuestion {
  widgetId: string;
  label: string;
  sectionId: string;
  sectionTitle: string;
  subSectionId: string;
}

/**
 * Every widget in the structure the AI compiler can fill, in document order,
 * carrying the section/subsection it lives under so the results panel can link
 * back to it.
 */
export function collectCompilableQuestions(
  structure: ModuleStructureObject | null,
): CompilableQuestion[] {
  if (!structure) return [];

  const questions: CompilableQuestion[] = [];

  for (const sectionId of structure.root.children) {
    const section = structure[sectionId];
    if (!isSectionStructureObject(section)) continue;

    for (const subSectionId of section.children) {
      const subSection = structure[subSectionId];
      if (!isSubSectionStructureObject(subSection)) continue;

      for (const widgetId of subSection.children) {
        const widget = structure[widgetId];
        if (!isWidgetStructureObject(widget)) continue;
        if (!AUTO_COMPILE_SUPPORTED_TYPES.includes(widget._mappedType))
          continue;

        questions.push({
          widgetId,
          label: widget.customization.description?.trim() || widgetId,
          sectionId,
          sectionTitle: section.customization.title,
          subSectionId,
        });
      }
    }
  }

  return questions;
}
